/**
 * InjuryTicker - Fixture/team-based injury carousel with slide + scroll animation
 *
 * Animation sequence per entry:
 *   1. Slide down (350ms)
 *   2. Pause — scales with player count so more injuries = more reading time
 *   3. Scroll horizontally at readable pace (proportional to content overflow)
 *   4. Pause at end — scales with player count
 *   5. Slide back up (350ms)
 *   6. Brief gap, then next entry
 *
 * Skip controls (always visible when >1 entry):
 *   Left:  ‹ (prev game, gray)  |  ⏮ LEAGUE (prev league, purple)
 *   Right: LEAGUE ⏭ (next league, purple)  |  › (next game, gray)
 *
 * Group modes:
 *   fixture — both teams' players shown together per matchup
 *   team    — one team per card (produces 2 cards per matchup)
 */

import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, Platform, Animated, Image, Easing, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTicker } from '../context/TickerContext';

const TICKER_HEIGHT = 36;
const SLIDE_DURATION = 350;
const SCROLL_SPEED = 40;        // px per second — comfortable reading pace
const GAP_BETWEEN_ENTRIES = 600; // ms gap between cards

// Dynamic pauses scale with how many players need to be read
const pauseBefore = () => 2500;                                   // Fixed — just time to read the header
const pauseAfter  = (count) => Math.max(2500, 800 + count * 800); // More players = more time at end

// Status color mapping
const STATUS_COLORS = {
    out:        { bg: 'rgba(239,68,68,0.25)',   border: 'rgba(239,68,68,0.6)',   text: '#ef4444', label: 'OUT'     },
    doubtful:   { bg: 'rgba(239,68,68,0.15)',   border: 'rgba(239,68,68,0.4)',   text: '#f87171', label: 'DOUBTFUL'},
    questionable:{ bg:'rgba(251,191,36,0.2)',   border: 'rgba(251,191,36,0.5)',  text: '#fbbf24', label: 'GTD'     },
    'day-to-day':{ bg:'rgba(251,191,36,0.15)',  border: 'rgba(251,191,36,0.4)',  text: '#fbbf24', label: 'DTD'     },
    dtd:        { bg: 'rgba(251,191,36,0.15)',  border: 'rgba(251,191,36,0.4)',  text: '#fbbf24', label: 'DTD'     },
    probable:   { bg: 'rgba(34,197,94,0.15)',   border: 'rgba(34,197,94,0.4)',   text: '#4ade80', label: 'PROB'    },
    suspended:  { bg: 'rgba(139,92,246,0.2)',   border: 'rgba(139,92,246,0.5)', text: '#a78bfa', label: 'SUSP'    },
    ir:         { bg: 'rgba(239,68,68,0.25)',   border: 'rgba(239,68,68,0.6)',   text: '#ef4444', label: 'IR'      },
    'il_60-day':{ bg: 'rgba(239,68,68,0.25)',   border: 'rgba(239,68,68,0.6)',   text: '#ef4444', label: 'IL-60'   },
    'il_10-day':{ bg: 'rgba(239,68,68,0.2)',    border: 'rgba(239,68,68,0.5)',   text: '#ef4444', label: 'IL-10'   },
    'il_15-day':{ bg: 'rgba(239,68,68,0.2)',    border: 'rgba(239,68,68,0.5)',   text: '#ef4444', label: 'IL-15'   },
    pup:        { bg: 'rgba(239,68,68,0.2)',    border: 'rgba(239,68,68,0.5)',   text: '#ef4444', label: 'PUP'     },
    nfi:        { bg: 'rgba(239,68,68,0.2)',    border: 'rgba(239,68,68,0.5)',   text: '#ef4444', label: 'NFI'     },
};
const getStatusStyle = (s) => STATUS_COLORS[(s || '').toLowerCase()] || STATUS_COLORS['questionable'];

const LEAGUE_EMOJI = { nba:'🏀', wnba:'🏀', ncaab:'🏀', nfl:'🏈', ncaaf:'🏈', mlb:'⚾', nhl:'🏒', mls:'⚽' };
const getLeagueEmoji = (l) => LEAGUE_EMOJI[(l || '').toLowerCase()] || '🏅';

const normalizeTeam = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/^(the|los|las|new|san|st\.?|saint)\s+/i, '').replace(/[^a-z0-9\s]/g, '').trim();
};

const teamMatches = (injuryTeam, fixtureTeam) => {
    if (!injuryTeam || !fixtureTeam) return false;
    const a = normalizeTeam(injuryTeam);
    const b = normalizeTeam(fixtureTeam);
    if (a === b) return true;
    if (a.length > 3 && b.includes(a)) return true;
    if (b.length > 3 && a.includes(b)) return true;
    const aLast = a.split(/\s+/).pop();
    const bLast = b.split(/\s+/).pop();
    if (aLast.length > 3 && aLast === bLast) return true;
    return false;
};

const getInjuryReason = (player) => {
    const d = player.injury_details || {};
    const parts = [];
    if (d.injury_type) parts.push(d.injury_type);
    if (d.body_part) parts.push(d.body_part);
    if (parts.length === 0 && d.designation) parts.push(d.designation);
    if (parts.length === 0 && d.description) parts.push(d.description);
    return parts.join(' — ') || '';
};

// Severity priority — lower = more severe
const SEVERITY_PRIORITY = { out: 0, doubtful: 1, questionable: 2, 'day-to-day': 3, dtd: 3, suspended: 4, probable: 5 };
const SEVERITY_THRESHOLD = {
    all: 99,
    questionable_up: 2,
    doubtful_up: 1,
    out_only: 0,
};

// ─────────────────────────────────────────────────────────────────────────────

const InjuryTicker = ({ isVisible = true }) => {
    const { fixtures, injuryTickerLeagues, injuryMinSeverity, injuryGroupBy } = useTicker();

    const slideY  = useRef(new Animated.Value(-TICKER_HEIGHT)).current;
    const opacity = useRef(new Animated.Value(0)).current;
    const scrollX = useRef(new Animated.Value(0)).current;

    const [injuries,     setInjuries]     = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Measured by onLayout on the content/container views
    const contentWidthRef   = useRef(0);
    const containerWidthRef = useRef(0);

    const timerRef     = useRef(null);
    const cancelledRef = useRef(false);

    // ─── Skip navigation ────────────────────────────────────────────────────
    const [skipSignal, setSkipSignal] = useState(0);
    const requestedIndexRef = useRef(0);

    const skipToEntry = useCallback((idx) => {
        requestedIndexRef.current = idx;
        setSkipSignal(s => s + 1);
    }, []);

    const skipToNext = useCallback((total) => {
        skipToEntry((currentIndex + 1) % total);
    }, [currentIndex, skipToEntry]);

    const skipToPrev = useCallback((total) => {
        skipToEntry((currentIndex - 1 + total) % total);
    }, [currentIndex, skipToEntry]);

    // ─── Injury data fetch ──────────────────────────────────────────────────
    const functionsUrl = 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';

    const fetchInjuries = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${functionsUrl}/getInjuryReport?league=all`, {
                method: 'GET', headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error('Failed');
            const data = await res.json();
            if (data.success && data.players) {
                const pri = { out: 0, doubtful: 1, questionable: 2, 'day-to-day': 3, suspended: 4, probable: 5 };
                setInjuries(
                    data.players
                        .filter(p => p.injury_status && p.injury_status !== 'active' && p.injury_status !== 'healthy')
                        .sort((a, b) => (pri[a.injury_status] ?? 10) - (pri[b.injury_status] ?? 10))
                );
            }
        } catch (err) {
            console.warn('[InjuryTicker] Fetch error:', err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInjuries();
        const iv = setInterval(fetchInjuries, 15 * 60 * 1000);
        return () => clearInterval(iv);
    }, [fetchInjuries]);

    // ─── Build display entries (fixture or per-team) ────────────────────────
    const displayEntries = useMemo(() => {
        if (!fixtures?.length || !injuries.length) return [];

        const severityThreshold = SEVERITY_THRESHOLD[injuryMinSeverity] ?? 99;
        const isTeamMode = injuryGroupBy === 'team';
        const entries = [];

        for (const fx of fixtures) {
            const home   = fx.home_team || '';
            const away   = fx.away_team || '';
            const league = (fx.league || fx.sport || '').toLowerCase();

            // League filter
            if (injuryTickerLeagues && injuryTickerLeagues.length > 0) {
                if (!injuryTickerLeagues.some(l => l.toLowerCase() === league)) continue;
            }

            let startTime = null;
            if (fx.start_date) {
                if (fx.start_date._seconds) startTime = new Date(fx.start_date._seconds * 1000);
                else if (fx.start_date.seconds) startTime = new Date(fx.start_date.seconds * 1000);
                else startTime = new Date(fx.start_date);
            }
            const timeStr = startTime
                ? startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                : '';

            const matchFilter = (p) => {
                const pl = (p.league || '').toLowerCase();
                if (pl && league && !pl.includes(league) && !league.includes(pl)) return false;
                const sev = SEVERITY_PRIORITY[(p.injury_status || '').toLowerCase()] ?? 99;
                return sev <= severityThreshold;
            };

            if (isTeamMode) {
                // One entry per team
                for (const teamName of [away, home]) {
                    const matched = injuries.filter(p =>
                        matchFilter(p) && teamMatches(p.team, teamName)
                    );
                    if (matched.length === 0) continue;
                    entries.push({
                        id: `${fx.fixture_id || fx.id}_${teamName}`,
                        teamMode: true,
                        team: teamName,
                        opponent: teamName === away ? home : away,
                        isHome: teamName === home,
                        home_team: home,
                        away_team: away,
                        league,
                        time: timeStr,
                        players: matched.slice(0, 12),
                        total: matched.length,
                    });
                }
            } else {
                // One entry per fixture (both teams together)
                const matched = injuries.filter(p =>
                    matchFilter(p) && (teamMatches(p.team, home) || teamMatches(p.team, away))
                );
                if (matched.length === 0) continue;
                entries.push({
                    id: fx.fixture_id || fx.id,
                    teamMode: false,
                    home_team: home,
                    away_team: away,
                    league,
                    time: timeStr,
                    players: matched.slice(0, 12),
                    total: matched.length,
                });
            }
        }
        return entries;
    }, [fixtures, injuries, injuryTickerLeagues, injuryMinSeverity, injuryGroupBy]);

    // ─── League skip helpers ─────────────────────────────────────────────────
    const uniqueLeagues = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const g of displayEntries) {
            if (!seen.has(g.league)) { seen.add(g.league); out.push(g.league); }
        }
        return out;
    }, [displayEntries]);

    const currentLeagueId = displayEntries[currentIndex]?.league ?? null;

    const skipToNextLeague = useCallback(() => {
        const total = displayEntries.length;
        if (total === 0) return;
        const curLeague = displayEntries[currentIndex]?.league;
        for (let i = 1; i <= total; i++) {
            const idx = (currentIndex + i) % total;
            if (displayEntries[idx]?.league !== curLeague) { skipToEntry(idx); return; }
        }
    }, [displayEntries, currentIndex, skipToEntry]);

    const skipToPrevLeague = useCallback(() => {
        const total = displayEntries.length;
        if (total === 0) return;
        const curLeague = displayEntries[currentIndex]?.league;
        // Find what league is "previous"
        let targetLeague = null;
        for (let i = 1; i <= total; i++) {
            const idx = (currentIndex - i + total) % total;
            if (displayEntries[idx]?.league !== curLeague) { targetLeague = displayEntries[idx]?.league; break; }
        }
        if (!targetLeague) return;
        // Jump to first entry of that league
        for (let i = 0; i < total; i++) {
            if (displayEntries[i]?.league === targetLeague) { skipToEntry(i); return; }
        }
    }, [displayEntries, currentIndex, skipToEntry]);

    const prevLeagueName = useMemo(() => {
        if (uniqueLeagues.length <= 1 || !currentLeagueId) return null;
        const i = uniqueLeagues.indexOf(currentLeagueId);
        const prev = uniqueLeagues[(i - 1 + uniqueLeagues.length) % uniqueLeagues.length];
        return prev !== currentLeagueId ? prev : null;
    }, [uniqueLeagues, currentLeagueId]);

    const nextLeagueName = useMemo(() => {
        if (uniqueLeagues.length <= 1 || !currentLeagueId) return null;
        const i = uniqueLeagues.indexOf(currentLeagueId);
        const next = uniqueLeagues[(i + 1) % uniqueLeagues.length];
        return next !== currentLeagueId ? next : null;
    }, [uniqueLeagues, currentLeagueId]);

    // ─── Wait helper (stored in timerRef for cleanup) ────────────────────────
    const wait = (ms) => new Promise(resolve => { timerRef.current = setTimeout(resolve, ms); });

    // ─── Main animation loop ─────────────────────────────────────────────────
    useEffect(() => {
        if (!isVisible || displayEntries.length === 0) {
            slideY.setValue(-TICKER_HEIGHT);
            opacity.setValue(0);
            return;
        }

        cancelledRef.current = false;

        const runCycle = async () => {
            let idx = requestedIndexRef.current % displayEntries.length;
            requestedIndexRef.current = 0;

            while (!cancelledRef.current) {
                const entry = displayEntries[idx % displayEntries.length];
                setCurrentIndex(idx % displayEntries.length);
                scrollX.setValue(0);

                // 1. Slide down
                slideY.setValue(-TICKER_HEIGHT);
                opacity.setValue(0);
                await new Promise(resolve => {
                    Animated.parallel([
                        Animated.timing(slideY, { toValue: 0, duration: SLIDE_DURATION, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
                        Animated.timing(opacity, { toValue: 1, duration: SLIDE_DURATION, useNativeDriver: true }),
                    ]).start(resolve);
                });
                if (cancelledRef.current) break;

                // 2. Pause before scroll — let user read header AND let layout settle
                //    (keying the content view to currentIndex ensures onLayout fires reliably)
                await wait(pauseBefore());
                if (cancelledRef.current) break;

                // Extra RAF + settle to make sure onLayout has fired for new content
                await new Promise(r => requestAnimationFrame(r));
                await wait(200);
                if (cancelledRef.current) break;

                // 3. Scroll through all players
                const overflow = Math.max(0, contentWidthRef.current - containerWidthRef.current);
                if (overflow > 0) {
                    const duration = Math.max(1000, (overflow / SCROLL_SPEED) * 1000);
                    await new Promise(resolve => {
                        const anim = Animated.timing(scrollX, {
                            toValue: -overflow,
                            duration,
                            easing: Easing.linear,
                            useNativeDriver: true,
                        });
                        anim.start(({ finished }) => {
                            if (!finished) scrollX.stopAnimation();
                            resolve();
                        });
                    });
                    if (cancelledRef.current) break;
                }

                // 4. Pause at end — scales with player count
                await wait(pauseAfter(entry.players.length));
                if (cancelledRef.current) break;

                // 5. Slide up
                await new Promise(resolve => {
                    Animated.parallel([
                        Animated.timing(slideY, { toValue: -TICKER_HEIGHT, duration: SLIDE_DURATION, easing: Easing.in(Easing.cubic), useNativeDriver: true }),
                        Animated.timing(opacity, { toValue: 0, duration: SLIDE_DURATION, useNativeDriver: true }),
                    ]).start(resolve);
                });
                if (cancelledRef.current) break;

                // 6. Gap
                await wait(GAP_BETWEEN_ENTRIES);
                idx++;
            }
        };

        runCycle();

        return () => {
            cancelledRef.current = true;
            scrollX.stopAnimation();
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [isVisible, displayEntries.length, skipSignal]);

    // ─── Render ───────────────────────────────────────────────────────────────
    if (!isVisible || (displayEntries.length === 0 && !loading)) return null;

    const current  = displayEntries[currentIndex] || displayEntries[0];
    const total    = displayEntries.length;
    const hasMulti = total > 1;
    const hasMultiLeague = uniqueLeagues.length > 1;

    return (
        <View style={styles.container}>
            {loading && injuries.length === 0 ? (
                <View style={styles.loadingRow}>
                    <Text style={styles.loadingText}>Loading injuries…</Text>
                </View>
            ) : current ? (
                <Animated.View style={[styles.slideRow, { transform: [{ translateY: slideY }], opacity }]}>

                    {/* ── Left label ── */}
                    <View style={styles.labelContainer}>
                        <Text style={styles.labelText}>🏥</Text>
                    </View>

                    {/* ── Left nav: prev league (outer) | prev fixture (inner) ── */}
                    {hasMulti && (
                        <View style={styles.leftControls}>
                            {/* Prev league — outer, skip icon + league name, purple */}
                            {hasMultiLeague && prevLeagueName && (
                                <TouchableOpacity
                                    onPress={skipToPrevLeague}
                                    style={styles.leagueBtn}
                                    hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                                    activeOpacity={0.7}
                                >
                                    <Ionicons name="play-skip-back" size={10} color="#a78bfa" />
                                    <Text style={styles.leagueBtnLabel}>{prevLeagueName.toUpperCase()}</Text>
                                </TouchableOpacity>
                            )}

                            {/* Separator */}
                            {hasMultiLeague && prevLeagueName && <View style={styles.btnSeparator} />}

                            {/* Prev game — inner, single chevron, gray */}
                            <TouchableOpacity
                                onPress={() => skipToPrev(total)}
                                style={styles.gameBtn}
                                hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                                activeOpacity={0.7}
                            >
                                <Ionicons name="chevron-back" size={12} color="rgba(255,255,255,0.55)" />
                                <Text style={styles.gameBtnLabel}>GAME</Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* ── Header: matchup or team name ── */}
                    <View style={styles.matchupContainer}>
                        <Text style={styles.matchupLeague}>{getLeagueEmoji(current.league)}</Text>
                        {current.teamMode ? (
                            <>
                                <Text style={styles.matchupText} numberOfLines={1}>{current.team}</Text>
                                <Text style={styles.matchupVs} numberOfLines={1}>vs {current.opponent}</Text>
                            </>
                        ) : (
                            <Text style={styles.matchupText} numberOfLines={1}>
                                {current.away_team} @ {current.home_team}
                            </Text>
                        )}
                        {current.time ? <Text style={styles.matchupTime}>{current.time}</Text> : null}
                    </View>

                    <View style={styles.verticalDivider} />

                    {/* ── Scrollable players ── */}
                    <View
                        style={styles.playersScroll}
                        onLayout={(e) => { containerWidthRef.current = e.nativeEvent.layout.width; }}
                    >
                        {/* key={currentIndex} forces remount on each fixture change → fresh onLayout */}
                        <Animated.View
                            key={currentIndex}
                            style={[styles.playersContent, { transform: [{ translateX: scrollX }] }]}
                            onLayout={(e) => { contentWidthRef.current = e.nativeEvent.layout.width; }}
                        >
                            {current.players.map((player, i) => {
                                const st     = getStatusStyle(player.injury_status);
                                const reason = getInjuryReason(player);
                                return (
                                    <View key={`${player.name}-${i}`} style={styles.playerChip}>
                                        {player.headshot_url && (
                                            <Image source={{ uri: player.headshot_url }} style={styles.playerPhoto} />
                                        )}
                                        <Text style={styles.playerName} numberOfLines={1}>{player.name}</Text>
                                        <View style={[styles.statusBadge, { backgroundColor: st.bg, borderColor: st.border }]}>
                                            <Text style={[styles.statusText, { color: st.text }]}>{st.label}</Text>
                                        </View>
                                        {reason ? <Text style={styles.reasonText} numberOfLines={1}>{reason}</Text> : null}
                                        {i < current.players.length - 1 && <View style={styles.chipDivider} />}
                                    </View>
                                );
                            })}
                            {current.total > 12 && (
                                <Text style={styles.moreText}>+{current.total - 12} more</Text>
                            )}
                        </Animated.View>
                    </View>

                    {/* ── Right nav: next fixture (inner) | next league (outer) ── */}
                    <View style={styles.rightControls}>
                        {hasMulti && (
                            <>
                                {/* Next game — inner, single chevron, gray */}
                                <TouchableOpacity
                                    onPress={() => skipToNext(total)}
                                    style={styles.gameBtn}
                                    hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                                    activeOpacity={0.7}
                                >
                                    <Text style={styles.gameBtnLabel}>GAME</Text>
                                    <Ionicons name="chevron-forward" size={12} color="rgba(255,255,255,0.55)" />
                                </TouchableOpacity>

                                {/* Separator */}
                                {hasMultiLeague && nextLeagueName && <View style={styles.btnSeparator} />}

                                {/* Next league — outer, skip icon + league name, purple */}
                                {hasMultiLeague && nextLeagueName && (
                                    <TouchableOpacity
                                        onPress={skipToNextLeague}
                                        style={styles.leagueBtn}
                                        hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                                        activeOpacity={0.7}
                                    >
                                        <Text style={styles.leagueBtnLabel}>{nextLeagueName.toUpperCase()}</Text>
                                        <Ionicons name="play-skip-forward" size={10} color="#a78bfa" />
                                    </TouchableOpacity>
                                )}
                            </>
                        )}

                        {/* Progress counter */}
                        <View style={styles.progressContainer}>
                            <Text style={styles.progressText}>{currentIndex + 1}/{total}</Text>
                        </View>
                    </View>

                </Animated.View>
            ) : null}
        </View>
    );
};

// ─────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
    container: {
        height: TICKER_HEIGHT,
        backgroundColor: 'transparent',
        overflow: 'hidden',
    },
    loadingRow: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        height: TICKER_HEIGHT,
    },
    loadingText: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 11,
        fontStyle: 'italic',
    },
    slideRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: TICKER_HEIGHT,
        width: '100%',
        backgroundColor: 'rgba(10,10,10,0.96)',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(239,68,68,0.12)',
    },
    labelContainer: {
        paddingHorizontal: 8,
        height: '100%',
        justifyContent: 'center',
        backgroundColor: 'rgba(239,68,68,0.08)',
        borderRightWidth: 1,
        borderRightColor: 'rgba(239,68,68,0.12)',
    },
    labelText: { fontSize: 12 },

    // ── Left/right control zones ──────────────────────────────────────────
    leftControls: {
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        borderRightWidth: 1,
        borderRightColor: 'rgba(255,255,255,0.07)',
    },
    rightControls: {
        flexDirection: 'row',
        alignItems: 'center',
        height: '100%',
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.07)',
    },

    // ── Fixture (game) skip buttons — gray ───────────────────────────────
    gameBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 5,
        height: '100%',
        gap: 2,
    },
    gameBtnLabel: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 7,
        fontWeight: '700',
        letterSpacing: 0.5,
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    },

    // ── League skip buttons — purple ──────────────────────────────────────
    leagueBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 6,
        height: '100%',
        gap: 3,
        backgroundColor: 'rgba(139,92,246,0.08)',
    },
    leagueBtnLabel: {
        color: '#a78bfa',
        fontSize: 8,
        fontWeight: '700',
        letterSpacing: 0.4,
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    },

    // ── Separator between game/league buttons ─────────────────────────────
    btnSeparator: {
        width: 1,
        height: 16,
        backgroundColor: 'rgba(255,255,255,0.08)',
    },

    // ── Matchup / team header ─────────────────────────────────────────────
    matchupContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 8,
        flexShrink: 0,
    },
    matchupLeague: { fontSize: 11 },
    matchupText: {
        color: '#FFFFFF',
        fontSize: 11,
        fontWeight: '700',
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
        maxWidth: 160,
    },
    matchupVs: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 10,
        fontWeight: '500',
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
        maxWidth: 100,
    },
    matchupTime: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 10,
        fontWeight: '500',
    },
    verticalDivider: {
        width: 1,
        height: 16,
        backgroundColor: 'rgba(255,255,255,0.08)',
        marginHorizontal: 4,
    },

    // ── Player scroll area ────────────────────────────────────────────────
    playersScroll: {
        flex: 1,
        height: '100%',
        overflow: 'hidden',
    },
    playersContent: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 3,
        paddingRight: 12,
        height: '100%',
        flexWrap: 'nowrap',
    },
    playerChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingRight: 2,
    },
    playerPhoto: {
        width: 18,
        height: 18,
        borderRadius: 9,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    playerName: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: 11,
        fontWeight: '600',
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
        maxWidth: 85,
    },
    statusBadge: {
        paddingHorizontal: 4,
        paddingVertical: 1,
        borderRadius: 3,
        borderWidth: 1,
    },
    statusText: {
        fontSize: 8,
        fontWeight: '800',
        letterSpacing: 0.3,
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    },
    reasonText: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 9,
        fontWeight: '500',
        fontStyle: 'italic',
        maxWidth: 80,
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    },
    chipDivider: {
        width: 1,
        height: 14,
        backgroundColor: 'rgba(255,255,255,0.06)',
        marginLeft: 2,
        marginRight: 2,
    },
    moreText: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 10,
        fontWeight: '600',
        fontStyle: 'italic',
        marginLeft: 4,
    },

    // ── Progress counter ──────────────────────────────────────────────────
    progressContainer: {
        paddingHorizontal: 6,
        height: '100%',
        justifyContent: 'center',
        borderLeftWidth: 1,
        borderLeftColor: 'rgba(255,255,255,0.05)',
    },
    progressText: {
        color: 'rgba(255,255,255,0.2)',
        fontSize: 9,
        fontWeight: '700',
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
    },
});

export default InjuryTicker;
